/**
 * M7 S38: the story world-content hooks (plan S37/S38). Each mission's world content (`missions/<id>.ts`) plugs into
 * the story service through one `StoryWorldContent`:
 *
 * - **`aliases`:** the story events a game event also means at a story site. The mission data names story refs
 *   (`ring:okafor-hideout`, `station:authority-seat`, `poi:okafor-lab`, `target:archive-relay`); the game reports real
 *   ids (a lane ring, an M2 station, a derelict POI, a relay). An alias is stepped like any other story event.
 * - **`afterEvent`:** the people logic after `stepMission` on every story event (Okafor's trust, risk and rescue;
 *   Hale's call; the Patrol's hail), with the worker's faction day.
 * - **`afterFork`:** what a fork commit does in the world beyond its data outcome (a bounty for running a stop).
 *
 * Event-driven (never per frame); it may allocate.
 */
import type { Career } from '../career/types'
import type { Galaxy } from '../galaxy/types'
import type { SystemLayout } from '../galaxy/layout/types'
import type { MissionContext } from '../story/missions'
import type { CareerStage, MissionId, StoryEvent, StoryResult } from '../story/types'
import type { GameEvent } from './events'
import type { StorySites } from './sites'

/** The Reach as world content reads it (null pieces outside the Reach). */
export interface WorldCtx {
  readonly galaxy: Galaxy
  readonly sites: StorySites
  layoutOf(systemId: string): SystemLayout
  /** A story hint or M3 anchor as a system id (`resolveSystemHint`). */
  resolve(hint: string): string | null
}

/** The worker facts the people logic reads (refreshed from `StoryView`; defaults when unknown). */
export interface WorldInput {
  /** The worker's faction day. */
  day: number
  /** Tethys extraction rigs in the Reach (AI and story); unknown = 1 (never asks for a mission rig blind). */
  tethysRigs: number
  /** The career stage now (the worker's corporation state). */
  stage: CareerStage
}

export interface StoryWorldContent {
  readonly id: string
  aliases?(career: Career, e: GameEvent, w: WorldCtx): StoryEvent[]
  afterEvent?(career: Career, ev: StoryEvent, input: WorldInput, ctx: MissionContext, w: WorldCtx | null): StoryResult<Career>
  afterFork?(career: Career, missionId: MissionId, forkId: string, choiceId: string, input: WorldInput): StoryResult<Career>
}

/** No change. */
export function unchanged(career: Career): StoryResult<Career> {
  return { career, commands: [], effects: [] }
}

/** `b` after `a`: b's Career, both lists in order. */
export function mergeResults(a: StoryResult<Career>, b: StoryResult<Career>): StoryResult<Career> {
  if (b.commands.length === 0 && b.effects.length === 0) return a.career === b.career ? a : { ...a, career: b.career }
  return {
    career: b.career,
    commands: [...a.commands, ...b.commands],
    effects: [...a.effects, ...b.effects],
  }
}

/** The system a station, lane ring or relay endpoint is in: true when `systemId` is the resolved `hint`'s system. */
export function inSystemOf(w: WorldCtx, hint: string, systemId: string): boolean {
  const sys = w.resolve(hint)
  return sys !== null && sys === systemId
}

/** True when the relay `relayId` has an end in the resolved `hint`'s system. */
export function relayTouches(w: WorldCtx, hint: string, relayId: string): boolean {
  const sys = w.resolve(hint)
  if (sys === null) return false
  const relay = w.galaxy.relays.find((r) => r.id === relayId)
  return relay !== undefined && (relay.a === sys || relay.b === sys)
}

/** The Career clock day (the day the kill and sale logs use). */
export function careerDay(career: Career): number {
  return Math.floor(career.gameTime / 86400)
}

/** Fills `{token}`s from `vars` (an unknown token stays as written). */
export function fillTokens(text: string, vars: Readonly<Record<string, string>>): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => vars[k] ?? m)
}
